// src/components/layout/Sidebar.jsx
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { getCurrentUser, signOut } from "../../utils/auth.js";
import { usePermissions } from "../../utils/permissions.js";
import UserMenu from "./UserMenu";

const ICONS = {
  dashboard: (className = "") => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  api: (className = "") => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="6" height="6" rx="1" />
      <rect x="15" y="4" width="6" height="6" rx="1" />
      <rect x="3" y="14" width="6" height="6" rx="1" />
      <path d="M9 7h6" />
      <path d="M12 10v4" />
      <path d="M12 14h6" />
    </svg>
  ),
  pos: (className = "") => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="5" width="12" height="16" rx="2" />
      <path d="M9 5V3h6v2" />
      <path d="M9 12h6" />
      <path d="M9 16h4" />
    </svg>
  ),
  routing: (className = "") => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="10" r="2" />
      <circle cx="6" cy="18" r="2" />
      <path d="M8 6h5a4 4 0 0 1 4 4" />
      <path d="M8 18h8" />
    </svg>
  ),
  mrp: (className = "") => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 5.5 12 3l2 2.5" />
      <path d="M4 9h16l-2 5H6z" />
      <path d="M6 14l-2 5h16l-2-5" />
    </svg>
  ),
  containers: (className = "") => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="7" width="18" height="10" rx="1.5" />
      <path d="M7 7v10" />
      <path d="M17 7v10" />
      <path d="M3 12h18" />
    </svg>
  ),
  operations: (className = "") => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 8a4 4 0 1 0 4 4 4 4 0 0 0-4-4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="M4.93 4.93 6.34 6.34" />
      <path d="M17.66 17.66 19.07 19.07" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="M4.93 19.07 6.34 17.66" />
      <path d="M17.66 6.34 19.07 4.93" />
    </svg>
  ),
  admin: (className = "") => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 20 6v6c0 5-4 9-8 10-4-1-8-5-8-10V6z" />
      <path d="M12 10v4" />
      <path d="M12 18h.01" />
    </svg>
  ),
  toolbox: (className = "") => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="10" rx="2" />
      <path d="M7 8V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
      <path d="M3 12h18" />
      <path d="M9 12v4" />
      <path d="M15 12v4" />
    </svg>
  ),
};

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const ChevronIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const ACTIVE_TOP = "bg-blue-50 text-blue-700 font-medium";
const INACTIVE_TOP = "text-gray-700 hover:bg-gray-100";
const CHILD_BASE = "flex items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100";

export default function Sidebar(props) {
  const { collapsed: collapsedProp, onToggle: onToggleProp } = props;

  const [collapsedLocal, setCollapsedLocal] = useState(false);
  const [openGroups, setOpenGroups] = useState({
    dashboard: true,
    operations: true,
    admin: true,
    api: true,
    pos: true,
    routing: true,
    toolbox: true,
    "api-wayli": true,
    edi: true,
  });

  const controlled = typeof collapsedProp === "boolean" && typeof onToggleProp === "function";
  const collapsed = controlled ? collapsedProp : collapsedLocal;
  const onToggle = controlled ? onToggleProp : () => setCollapsedLocal((v) => !v);

  const navigate = useNavigate();
  const location = useLocation();
  const me = getCurrentUser();
  const { getLevel } = usePermissions();
  const roleName = me?.role;
  const [ingestWarningCount, setIngestWarningCount] = useState(null);

  const widthCls = collapsed ? "w-16" : "w-52";
  const iconClass = "h-5 w-5 shrink-0";
  const topBase = collapsed
    ? "flex items-center justify-center rounded-md px-3 py-2 text-sm transition-colors"
    : "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors";

  const isPathActive = (path) => {
    if (!path) return false;
    if (location.pathname === path) return true;
    return location.pathname.startsWith(`${path}/`);
  };

  const hasActiveChild = (items = []) =>
    items.some((child) => (child.children ? hasActiveChild(child.children) : isPathActive(child.to)));

  const expandSidebar = () => {
    if (collapsed) {
      if (controlled) {
        onToggle();
      } else {
        setCollapsedLocal(false);
      }
    }
  };

  const handleLogout = async () => {
    const unsaved = localStorage.getItem("pp_unsaved_mycontainers") === "1";
    if (unsaved) {
      const leave = window.confirm("You have unsaved changes in My Containers.\nLeave without saving?");
      if (!leave) return;
    }

    await signOut({ revokeRemote: true });
    navigate("/login", { replace: true });
  };

  const handleGroupToggle = (key) => {
    if (collapsed) {
      expandSidebar();
      setOpenGroups((prev) => ({ ...prev, [key]: true }));
      return;
    }
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const allowed = getLevel(roleName, "admin-ingest-warnings") !== "None";
    if (!allowed) {
      setIngestWarningCount(null);
      return undefined;
    }
    if (!me?.email || !me?.role) {
      setIngestWarningCount(null);
      return undefined;
    }

    let cancelled = false;
    const loadCount = async () => {
      try {
        const headers = { "Content-Type": "application/json" };
        headers["X-User-Email"] = me.email;
        headers["X-User-Role"] = me.role;
        const resp = await fetch(
          `${API_BASE}/api/admin/mrp-product-ingest-warnings?status=OPEN&limit=1&offset=0`,
          { headers }
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data?.ok !== true) {
          throw new Error(data?.error || "Fetch failed");
        }
        const nextCount = Number.isFinite(data.total)
          ? data.total
          : Array.isArray(data.items)
          ? data.items.length
          : 0;
        if (!cancelled) {
          setIngestWarningCount(nextCount);
        }
      } catch (err) {
        if (!cancelled) {
          setIngestWarningCount(null);
        }
      }
    };

    loadCount();
    return () => {
      cancelled = true;
    };
  }, [getLevel, roleName, me?.email, me?.role]);

  const baseNavItems = [
    { key: "developers", label: "Developers", to: "/developers", icon: "api" },
    { key: "my-containers", label: "My Containers", to: "/my-containers", icon: "containers", pageId: "my-containers" },
  ].filter((item) => !item.pageId || getLevel(roleName, item.pageId) !== "None");

  const navItems = [];

  const dashboardChildren = [
    getLevel(roleName, "dashboard") !== "None"
      ? { key: "dashboard-home", label: "Dashboard", to: "/dashboard", pageId: "dashboard" }
      : null,
    getLevel(roleName, "pos") !== "None" || getLevel(roleName, "routing") !== "None"
      ? { key: "po-routing-dashboard", label: "PO + Routing Dashboard", to: "/dashboard/po-routing" }
      : null,
    getLevel(roleName, "mrp-container-details") !== "None"
      ? { key: "container-dashboard", label: "Container Dashboard", to: "/mrp/container-dashboard", pageId: "mrp-container-details" }
      : null,
  ].filter(Boolean);

  if (dashboardChildren.length > 0) {
    navItems.push({
      key: "dashboard",
      label: "Dashboard",
      icon: "dashboard",
      children: dashboardChildren,
    });
  }

  navItems.push(...baseNavItems);

  const ediChildren = [
    { key: "edi-inbox", label: "EDI Inbox", to: "/edi/inbox", pageId: "edi-inbox" },
    { key: "edi-997-drafts", label: "997 Drafts", to: "/edi/997-drafts", pageId: "edi-997-drafts" },
    { key: "edi-po-status", label: "PO Status", to: "/edi/po-status", pageId: "edi-po-status" },
    { key: "edi-856", label: "Walmart 856", to: "/edi/856", pageId: "edi-856" },
    { key: "edi-810", label: "Walmart 810", to: "/edi/810", pageId: "edi-810" },
  ].filter((child) => getLevel(roleName, child.pageId) !== "None");
  if (ediChildren.length > 0) navItems.push({ key: "edi", label: "EDI", icon: "api", children: ediChildren });

  const toolboxChildren = [
    { key: "sandbox-pos", label: "PO Details<Sandbox mode>", to: "/toolbox/pos", pageId: "pos" },
    { key: "sandbox-routing", label: "Routing Status<Sandbox mode>", to: "/toolbox/routing-status", pageId: "routing" },
  ].filter((child) => getLevel(roleName, child.pageId) !== "None");

  if (toolboxChildren.length > 0) {
    navItems.push({
      key: "toolbox",
      label: "Toolbox",
      icon: "toolbox",
      children: toolboxChildren,
    });
  }

  const poChildren = [
    { key: "po-details", label: "PO Details", to: "/pos", pageId: "pos" },
    { key: "pending-wms-entry", label: "Pending WMS Entry", to: "/pos/pending-wms-entry", pageId: "pending-wms-entry" },
    { key: "pricing", label: "Pricing", to: "/pricing", pageId: "pricing" },
  ].filter((child) => getLevel(roleName, child.pageId) !== "None");

  if (poChildren.length > 0) {
    const poGroup = {
      key: "pos",
      label: "POs",
      icon: "pos",
      children: poChildren,
    };
    const insertIndex = navItems.findIndex((item) => item.key === "my-containers");
    if (insertIndex === -1) navItems.push(poGroup);
    else navItems.splice(insertIndex, 0, poGroup);
  }

  const routingChildren = [
    { key: "routing-x-pos", label: "Routing X POs", to: "/routing/routing-x-pos", pageId: "routing-x-pos" },
    { key: "routing-status", label: "Routing Status", to: "/routing/routing-status", pageId: "routing" },
  ].filter((child) => getLevel(roleName, child.pageId) !== "None");

  if (routingChildren.length > 0) {
    const routingGroup = {
      key: "routing",
      label: "Routing",
      icon: "routing",
      children: routingChildren,
    };
    const insertIndex = navItems.findIndex((item) => item.key === "mrp" || item.key === "my-containers");
    if (insertIndex === -1) navItems.push(routingGroup);
    else navItems.splice(insertIndex, 0, routingGroup);
  }

  const mrpChildren = [
    { key: "mrp-aio", label: "MRP AIO", to: "/mrp/mrp-aio", pageId: "mrp" },
    { key: "mrp-inventory", label: "Inventory", to: "/mrp/inventory", pageId: "mrp-inventory" },
    { key: "mrp-container-details", label: "Container Details", to: "/mrp/container-details", pageId: "mrp-container-details" },
    { key: "mrp-products", label: "Products", to: "/mrp/product", pageId: "mrp-products" },
    { key: "mrp-walmart-ladder", label: "Walmart Ladder", to: "/mrp/walmart-ladder", pageId: "mrp-walmart-ladder" },
  ].filter((child) => getLevel(roleName, child.pageId) !== "None");

  if (mrpChildren.length > 0) {
    const insertIndex = navItems.findIndex((item) => item.key === "my-containers");
    const group = {
      key: "mrp",
      label: "MRP",
      icon: "mrp",
      children: mrpChildren,
    };
    if (insertIndex === -1) {
      navItems.push(group);
    } else {
      navItems.splice(insertIndex, 0, group);
    }
  }

  const wayliChildren = [
    { key: "raw-vessels", label: "raw_vessels", to: "/api/wayli/raw-vessels", pageId: "raw-vessels" },
    { key: "raw-vessel-containers", label: "raw_vessel_containers", to: "/api/wayli/raw-vessel-containers", pageId: "raw-vessel-containers" },
    { key: "raw-vessel-consignees", label: "raw_vessel_consignees", to: "/api/wayli/raw-vessel-consignees", pageId: "raw-vessel-consignees" },
  ].filter((child) => getLevel(roleName, child.pageId) !== "None");

  const apiChildren = [];
  if (wayliChildren.length > 0) {
    apiChildren.push({
      key: "api-wayli",
      label: "Wayli ERP",
      children: wayliChildren,
    });
  }

  if (apiChildren.length > 0) {
    navItems.push({
      key: "api",
      label: "API",
      icon: "api",
      children: apiChildren,
    });
  }

  const operationsChildren = [
    { key: "drayage", label: "Drayage", to: "/admin/drayage", pageId: "admin-drayage" },
    { key: "warehouse", label: "Warehouse", to: "/admin/warehouse", pageId: "admin-warehouse" },
    { key: "ship-points", label: "Ship Points", to: "/admin/ship-points", pageId: "admin-ship-points" },
    { key: "customers", label: "Customers", to: "/admin/customers", pageId: "admin-customers" },
  ].filter((child) => getLevel(roleName, child.pageId) !== "None");

  if (operationsChildren.length > 0) {
    navItems.push({
      key: "operations",
      label: "Operations",
      icon: "operations",
      children: operationsChildren,
    });
  }

  const adminChildren = [
    { key: "admin-home", label: "Admin Home", to: "/admin", pageId: "admin-home" },
    { key: "admin-roles", label: "Roles", to: "/admin/roles", pageId: "admin-roles" },
    { key: "users", label: "Users", to: "/admin/users", pageId: "admin-users" },
    { key: "admin-logs", label: "Log", to: "/admin/logs", pageId: "admin-logs" },
    { key: "admin-api-logs", label: "API Logs", to: "/admin/api-logs", pageId: "admin-api-logs" },
    { key: "admin-pricing-overrides", label: "Pricing Overrides", to: "/admin/pricing-overrides", pageId: "admin-pricing-overrides" },
    { key: "admin-ingest-warnings", label: "Ingest Warnings", to: "/admin/ingest-warnings", pageId: "admin-ingest-warnings" },
    { key: "admin-product-suggestions", label: "Product Suggestions", to: "/admin/product-suggestions", pageId: "admin-product-suggestions" },
    { key: "admin-snapshots", label: "Snapshots", to: "/admin/snapshots", pageId: "admin-snapshots" },
    { key: "cust-api-mappings", label: "API Mappings", to: "/admin/api-mappings", pageId: "admin-cust-api-mappings" },
    { key: "api-manager", label: "API Manager", to: "/admin/api-manager", pageId: "admin-api-manager" },
    { key: "walmart-dc-address-book", label: "Walmart DC Address Book", to: "/admin/walmart-dc-address-book", pageId: "admin-walmart-dc-address-book" },
  ].filter((child) => getLevel(roleName, child.pageId) !== "None");

  if (adminChildren.length > 0) {
    navItems.push({
      key: "admin",
      label: "Admin",
      icon: "admin",
      children: adminChildren,
    });
  }

  return (
    <aside
      className={`${widthCls} h-screen sticky top-0 shrink-0 border-r bg-white flex flex-col justify-between transition-all duration-200 z-[200]`}
    >
      <div className="p-3 flex items-center justify-between">
        <div className={`text-lg font-bold truncate ${collapsed ? "hidden" : "block"}`}>PortPilot</div>
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand" : "Collapse"}
          className="h-7 w-7 grid place-items-center rounded-md border text-gray-600 hover:bg-gray-50"
        >
          {collapsed ? (
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 6l6 6-6 6" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 6l-6 6 6 6" />
            </svg>
          )}
        </button>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 space-y-1">
        {navItems.map((item) => {
          const Icon = ICONS[item.icon] || (() => null);

          if (item.children) {
            const isOpen = openGroups[item.key] ?? true;
            const childActive = hasActiveChild(item.children);
            const topClasses = `${topBase} ${childActive || isOpen ? ACTIVE_TOP : INACTIVE_TOP}`;

            return (
              <div key={item.key} className="mt-2 first:mt-0">
                <button
                  type="button"
                  onClick={() => handleGroupToggle(item.key)}
                  className={topClasses}
                  title={item.label}
                >
                  <span className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
                    {Icon(iconClass)}
                    {!collapsed && <span className="font-medium">{item.label}</span>}
                  </span>
                  {!collapsed && (
                    <ChevronIcon
                      className={`h-3.5 w-3.5 ml-auto transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                  )}
                </button>

                {!collapsed && isOpen && (
                  <div className="mt-1 space-y-1 pl-8">
                    {item.children.map((child) =>
                      child.children ? (
                        <div key={child.key} className="space-y-1">
                          <button
                            type="button"
                            onClick={() => handleGroupToggle(child.key)}
                            className={`${CHILD_BASE} justify-between ${hasActiveChild(child.children) ? ACTIVE_TOP : ""}`}
                            title={child.label}
                          >
                            <span className="font-medium">{child.label}</span>
                            <ChevronIcon
                              className={`h-3.5 w-3.5 transition-transform ${openGroups[child.key] ?? true ? "rotate-90" : ""}`}
                            />
                          </button>
                          {(openGroups[child.key] ?? true) && (
                            <div className="space-y-1 pl-5">
                              {child.children.map((grand) => (
                                <NavLink
                                  key={grand.key}
                                  to={grand.to}
                                  className={({ isActive }) =>
                                    `${CHILD_BASE} ${isActive || isPathActive(grand.to) ? ACTIVE_TOP : ""}`
                                  }
                                  title={grand.label}
                                >
                                  <span>{grand.label}</span>
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <NavLink
                          key={child.key}
                          to={child.to}
                          className={({ isActive }) =>
                            `${CHILD_BASE} ${isActive || isPathActive(child.to) ? ACTIVE_TOP : ""}`
                          }
                          title={child.label}
                        >
                          <span className="flex items-center gap-2">
                            <span>{child.label}</span>
                            {child.key === "admin-ingest-warnings" &&
                              !collapsed &&
                              typeof ingestWarningCount === "number" && (
                                <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                                  {ingestWarningCount}
                                </span>
                              )}
                          </span>
                        </NavLink>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          }

          return (
            <NavLink
              key={item.key}
              to={item.to}
              className={({ isActive }) => `${topBase} ${isActive ? ACTIVE_TOP : INACTIVE_TOP}`}
              title={item.label}
            >
              {Icon(iconClass)}
              {!collapsed && <span className="font-medium">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-2 pt-2 pb-3 border-t flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={handleLogout}
          title="Logout"
          aria-label="Logout"
          className="h-9 w-9 grid place-items-center rounded-md border text-gray-600 hover:bg-gray-50"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="M16 17l5-5-5-5" />
            <path d="M21 12H9" />
          </svg>
        </button>

        <UserMenu />
      </div>
    </aside>
  );
}

